<!--
  ExecutiveSidebar — contextual sidebar for the Executive cauldron.

  Layout (top → bottom): one section per tool category, each rendering its
  label then one row per tool, deep-linked to the tool's own route.
    - Forecasts (future)    — forward-looking models.
    - Benchmarks (past)     — retrospective analysis.
    - Simulators (what-if)  — hypothetical scenarios.
    - Monitors (present)    — live, read-only market observation.

  There is no "Tools" header or "Overzicht" entry: clicking Tools in the
  main sidebar already lands on the /tools overview page, so a duplicate
  overview link here would be redundant.

  Per-tool entries are sourced from `src/config/domain/toolKeys.js` (the
  canonical registry) via `toolsByCategory()`, so adding a new tool to
  that file automatically extends this sidebar without a separate edit.
-->
<template>
  <div class="flex flex-col h-full">
    <nav class="flex-1 overflow-y-auto px-3 py-3" aria-label="Executive navigation">
      <!-- Category sections — each renders its label then one row per
           tool. Sections without tools render nothing (e.g. when a
           category is fully feature-flagged off in some future state). -->
      <template
        v-for="section in categorySections"
        :key="section.key"
      >
        <div v-if="!collapsed" class="sidebar-section-label">
          {{ t(section.titleKey) }}
        </div>
        <sidebar-menu-item
          v-for="tool in section.tools"
          :key="tool.key"
          :to="tool.deepLink"
          :label="toolLabel(tool, t, te)"
          :icon="tool.icon"
          :stage="tool.stage"
          :collapsed="collapsed"
          active-match="prefix"
          @click="emit('navigate')"
        />
      </template>
    </nav>
  </div>
</template>

<script setup>
  /* eslint-disable no-undef */
  import SidebarMenuItem from '@/components/navigation/SidebarMenuItem.vue';
  import { useFeatureFlags } from '@/composables/useFeatureFlags';
  import { CATEGORY_META, filterToolsByFeatureFlag, toolLabel, toolsByCategory } from '@/config/domain/toolKeys';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  defineProps({
    collapsed: { type: Boolean, default: false },
  });
  const emit = defineEmits(['navigate']);

  const { t, te } = useI18n();
  const { isFeatureEnabled } = useFeatureFlags();

  // Group the canonical tool list by category once per render, then drop any
  // tool whose feature flag is disabled — otherwise the link would render but
  // its route guard (`meta.requiresFeatureFlag`) would bounce the user home.
  // Cheap (~10 entries) so we don't bother memoising further.
  const grouped = computed(() => toolsByCategory());

  // Ordered category sections. Order is deliberate and follows a time logic:
  // forecasts (future) → benchmarks (past) → simulators (what-if) →
  // monitors (present, live read-only observation).
  const ORDER = ['forecasts', 'benchmarks', 'simulators', 'monitors'];
  const categorySections = computed(() =>
    ORDER.map((key) => ({
      key,
      titleKey: CATEGORY_META[key]?.titleKey || `tools_collection.categories.${key}`,
      tools: filterToolsByFeatureFlag(grouped.value[key] || [], isFeatureEnabled),
    })).filter((section) => section.tools.length > 0),
  );
</script>
